/**
 * 页面内动作级权限统一入口（rbac-menu-key-alignment:SC-07 动作粒度口径）。
 *
 * 页面级可达性由路由守卫 RequireMenuPermission（VIEW 粒度）负责；
 * 页面内的按钮/操作入口（新增、导出、冲正等）统一用本组件收敛，
 * 避免各页面自行解构 useSession().permissions 调 hasMenuAction 的散点写法。
 *
 * 快照缺失（null，拉取失败/加载中）时 hasMenuAction 走岗位兜底——与菜单可见口径一致，
 * 不额外 fail-closed（页面本身已被路由守卫拦过一道）。
 */
import type { ReactNode } from 'react';
import { useSession } from './SessionContext';
import { hasMenuAction } from '../api/auth.api';

/** 判定当前会话是否拥有指定菜单键动作；hook 版（驱动条件渲染/逻辑分支）。 */
export function useHasPermission(menuKey: string, action: string): boolean {
  const { permissions } = useSession();
  return hasMenuAction(permissions, menuKey, action);
}

interface HasPermissionProps {
  /** 菜单标识（如 ADM-09） */
  menuKey: string;
  /** 动作（VIEW/CREATE/EDIT/DELETE/EXPORT/COLLECT/REVERSE/GRANT/MAKEUP） */
  action: string;
  /** 有权限时渲染的内容 */
  children: ReactNode;
  /** 无权限时的替代内容（默认不渲染） */
  fallback?: ReactNode;
}

/** 条件渲染版：有权限渲染 children，无权限渲染 fallback（缺省 null）。 */
export function HasPermission({ menuKey, action, children, fallback = null }: HasPermissionProps) {
  const allowed = useHasPermission(menuKey, action);
  return <>{allowed ? children : fallback}</>;
}
